import { useState, useMemo } from "react";
import { RBTag } from "@/pages/raise-board/rb-ui";
import { DefList, DefRow, PortalSection } from "./portal-ui";
import {
  getStageText,
  DistinctionOptions,
  GICSSubIndustries,
  RevenueModels,
  filterPlaceholderTagValues,
} from "@/lib/utils";

interface EligibilityCriteriaCardProps {
  // Industry tags (level 5 sub-industry codes)
  gicsIndustries?: string[];
  gicsSubIndustries?: string[];
  // Revenue models
  revenueModels?: string[];
  // Stage thesis — accepts legacy numeric entries for workspaces written
  // before the string-stage migration; getStageText canonicalises both.
  stages?: Array<string | number>;
  locations?: string[];
  businessModels?: string[];
  distinctions?: string[];
}

function getDistinctionText(value: string) {
  const found = DistinctionOptions.find((d) => d.value === value);
  return found ? found.text : value;
}

/** Long lists (industries, mostly) show the first few and expand on request. */
const COLLAPSED_COUNT = 10;

function TagRow({ title, items }: { title: string; items: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, COLLAPSED_COUNT);
  const hidden = items.length - shown.length;

  return (
    <DefRow term={title}>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((item) => (
          <RBTag key={item}>{item}</RBTag>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded-md px-1.5 py-px text-[12px] font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
          >
            Show all {items.length}
          </button>
        )}
      </div>
    </DefRow>
  );
}

export function EligibilityCriteriaCard({
  gicsIndustries = [],
  gicsSubIndustries = [],
  revenueModels,
  stages,
  locations,
  businessModels,
  distinctions,
}: EligibilityCriteriaCardProps) {
  // Resolve industry tag names from sub-industry codes
  const industryTagNames = useMemo(() => {
    return gicsSubIndustries
      .map((code) => GICSSubIndustries.find((si) => si.code === code)?.name || code)
      .sort();
  }, [gicsSubIndustries]);

  // Scrub placeholder sentinels (see filterPlaceholderTagValues) so
  // `["no information provided"]` is treated as empty and the section hides.
  const cleanRevenueModels = filterPlaceholderTagValues(revenueModels);
  const cleanLocations = filterPlaceholderTagValues(locations);
  const cleanBusinessModels = filterPlaceholderTagValues(businessModels);
  const cleanDistinctions = filterPlaceholderTagValues(distinctions);
  const cleanStages = (stages ?? []).filter(
    (s) =>
      typeof s === "number" ||
      (typeof s === "string" && s.trim() !== ""),
  );

  const hasIndustryTags = industryTagNames.length > 0 || gicsIndustries.length > 0;
  const hasRevenueModels = cleanRevenueModels.length > 0;
  const hasStages = cleanStages.length > 0;
  const hasLocations = cleanLocations.length > 0;
  const hasBusinessModels = cleanBusinessModels.length > 0;
  const hasDistinctions = cleanDistinctions.length > 0;

  // Hide card if ALL criteria are missing
  const hasCriteria =
    hasIndustryTags ||
    hasRevenueModels ||
    hasStages ||
    hasLocations ||
    hasBusinessModels ||
    hasDistinctions;

  if (!hasCriteria) return null;

  // Map revenue model values to labels
  const revenueModelLabels = cleanRevenueModels.map(
    (value) => RevenueModels.find((rm) => rm.value === value)?.label || value
  );

  // Map stage numbers to text
  const stageLabels = cleanStages.map((stage) => getStageText(stage));

  const meaningfulDistinctions = cleanDistinctions.filter(
    (d) => d !== "Other" && d !== "Not Applicable"
  );

  // A heading over no rows reads as broken; skip the section instead.
  const hasRows =
    hasStages ||
    industryTagNames.length > 0 ||
    hasLocations ||
    hasBusinessModels ||
    hasRevenueModels ||
    meaningfulDistinctions.length > 0;
  if (!hasRows) return null;

  return (
    <PortalSection title="Who we back">
      <DefList>
        {hasStages && <TagRow title="Stage" items={stageLabels} />}
        {hasIndustryTags && industryTagNames.length > 0 && (
          <TagRow title="Industries" items={industryTagNames} />
        )}
        {hasLocations && <TagRow title="Geography" items={cleanLocations} />}
        {hasBusinessModels && (
          <TagRow title="Business model" items={cleanBusinessModels} />
        )}
        {hasRevenueModels && (
          <TagRow title="Revenue model" items={revenueModelLabels} />
        )}
        {hasDistinctions && meaningfulDistinctions.length > 0 && (
          <TagRow
            title="Founder diversity"
            items={meaningfulDistinctions.map((d) => getDistinctionText(d))}
          />
        )}
      </DefList>
    </PortalSection>
  );
}
